/**
 * App chrome. Below `DESKTOP` it is the bottom tab bar; above, a 236px sidebar
 * with a 70px content header.
 *
 * The vertical rule before the Tasks tab is load-bearing, not decoration: four
 * tabs read, the fifth acts. The progress ring lives only on the fifth.
 *
 * Layout note that matters more than it looks: the desktop frame is a flex row
 * whose content column carries `minWidth: 0`. Without it a flex/grid child
 * defaults to `min-width: auto`, so one wide row (eight 88px direction cards, a
 * long unbroken word) stretches the column past the viewport and the whole page
 * gains a horizontal scrollbar. Every column helper below re-states it.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useT, TFn } from '../i18n/useT';
import { Icon } from './icons';
import { Ring } from './orb';
import { Col, Meta, Row, useIsDesktop } from './primitives';
import { c, hair, f, ACT, DESKTOP } from './tokens';
import { R } from './routes';

export type TabId = 'state' | 'dirs' | 'pats' | 'ins' | 'tasks';

interface Tab {
    id: TabId;
    /** Translation key — the label is resolved where it renders. */
    label: string;
    icon: string;
    to: string;
}

const READ: Tab[] = [
    { id: 'state', label: 'app.tab.state', icon: 'tState', to: R.state },
    { id: 'dirs', label: 'app.tab.dirs', icon: 'tDirs', to: R.dirs },
    { id: 'pats', label: 'app.tab.pats', icon: 'tPats', to: R.pats },
    { id: 'ins', label: 'app.tab.ins', icon: 'tIns', to: R.ins },
];

const TASKS: Tab = { id: 'tasks', label: 'app.tab.tasks', icon: 'tTasks', to: R.tasks };

export const Avatar: React.FC<{ score: number | null; letter?: string; size?: number }> = ({
    score,
    letter = '·',
    size = 36,
}) => (
    <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
        <Ring score={score} size={size} sw={2.2} neutral={score == null} />
        <div
            style={{
                position: 'absolute',
                inset: size * 0.14,
                borderRadius: '50%',
                background: c.pill,
                display: 'grid',
                placeItems: 'center',
                fontSize: size * 0.36,
                fontWeight: 400,
                color: c.textPri,
            }}
        >
            {letter}
        </div>
    </div>
);

const ProgressRing: React.FC<{ done: number; total: number; on: boolean; size?: number }> = ({
    done,
    total,
    on,
    size = 34,
}) => {
    const r = size / 2 - 1.6;
    const cc = 2 * Math.PI * r;
    const frac = total ? done / total : 0;

    return (
        <div
            style={{
                position: 'relative',
                width: size,
                height: size,
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
            }}
        >
            <svg width={size} height={size} style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={r} stroke={c.track} strokeWidth="2.4" fill="none" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke={on ? c.textPri : ACT}
                    strokeWidth="2.4"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={cc}
                    strokeDashoffset={cc - frac * cc}
                />
            </svg>
            <span
                style={{
                    fontFamily: f.mono,
                    fontSize: size < 30 ? 9 : 11,
                    color: on ? c.textPri : 'rgba(232,230,224,0.62)',
                }}
            >
                {done}
            </span>
        </div>
    );
};

const TabBar: React.FC<{ active: TabId; done: number; total: number; t: TFn }> = ({ active, done, total, t }) => (
    <nav
        style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 40,
            background: 'rgba(9,9,9,0.92)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: hair,
            padding: '8px 10px calc(10px + env(safe-area-inset-bottom, 12px))',
        }}
    >
        <Row>
            {READ.map((tab) => {
                const on = active === tab.id;

                return (
                    <Link key={tab.id} to={tab.to} style={{ flex: 1, textDecoration: 'none' }}>
                        <Col style={{ alignItems: 'center' }} gap={5}>
                            <Icon k={tab.icon} size={21} color={on ? c.textPri : 'rgba(232,230,224,0.4)'} />
                            <span
                                style={{
                                    fontFamily: f.mono,
                                    fontSize: 9.5,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: on ? c.textPri : 'rgba(232,230,224,0.4)',
                                }}
                            >
                                {t(tab.label)}
                            </span>
                        </Col>
                    </Link>
                );
            })}
            <div
                style={{
                    width: 1,
                    alignSelf: 'stretch',
                    background: 'rgba(232,230,224,0.12)',
                    margin: '2px 10px 2px 6px',
                }}
            />
            <Link to={TASKS.to} style={{ width: 52, textDecoration: 'none' }}>
                <Col style={{ alignItems: 'center' }} gap={5}>
                    <ProgressRing done={done} total={total} on={active === 'tasks'} />
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 9,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: active === 'tasks' ? c.textPri : 'rgba(232,230,224,0.4)',
                        }}
                    >
                        {t(TASKS.label)}
                    </span>
                </Col>
            </Link>
        </Row>
    </nav>
);

const SideItem: React.FC<{ tab: Tab; on: boolean; t: TFn }> = ({ tab, on, t }) => (
    <Link to={tab.to} style={{ textDecoration: 'none' }}>
        <Row
            style={{
                gap: 12,
                height: 40,
                padding: '0 12px',
                borderRadius: 11,
                background: on ? 'rgba(232,230,224,0.07)' : 'transparent',
                transition: 'background 200ms',
            }}
        >
            <Icon k={tab.icon} size={17} color={on ? c.textPri : 'rgba(232,230,224,0.45)'} />
            <span style={{ fontSize: 13.5, fontWeight: 400, color: on ? c.textPri : c.muted }}>{t(tab.label)}</span>
        </Row>
    </Link>
);

const Sidebar: React.FC<{
    active: TabId;
    done: number;
    total: number;
    index: number | null;
    email?: string;
    t: TFn;
}> = ({ active, done, total, index, email, t }) => (
    <Col
        style={{
            width: 236,
            flex: '0 0 auto',
            borderRight: hair,
            padding: '26px 0 22px',
            background: '#0c0b0b',
            position: 'sticky',
            top: 0,
            height: '100vh',
        }}
    >
        <Link to="/" style={{ textDecoration: 'none' }}>
            <Row style={{ gap: 10, padding: '0 22px 26px' }}>
                <div
                    style={{
                        width: 26,
                        height: 26,
                        borderRadius: 8,
                        border: `1px solid ${ACT}55`,
                        background: `${ACT}12`,
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                    }}
                >
                    <Icon k="tState" size={14} color={ACT} />
                </div>
                <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: '-0.015em', color: c.textPri }}>
                    DoYourStep
                </span>
            </Row>
        </Link>

        <Col gap={2} style={{ padding: '0 12px' }}>
            {READ.map((tab) => (
                <SideItem key={tab.id} tab={tab} on={active === tab.id} t={t} />
            ))}
        </Col>

        <div style={{ margin: '14px 22px', borderTop: hair }} />

        <div style={{ padding: '0 12px' }}>
            <Link to={TASKS.to} style={{ textDecoration: 'none' }}>
                <Row
                    style={{
                        gap: 12,
                        height: 44,
                        padding: '0 12px',
                        borderRadius: 11,
                        background: active === 'tasks' ? 'rgba(232,230,224,0.07)' : 'transparent',
                        border: active === 'tasks' ? 'none' : `1px solid ${ACT}26`,
                    }}
                >
                    <ProgressRing done={done} total={total} on={active === 'tasks'} size={24} />
                    <span
                        style={{
                            fontSize: 13.5,
                            fontWeight: 500,
                            color: active === 'tasks' ? c.textPri : c.text,
                            flex: 1,
                        }}
                    >
                        {t(TASKS.label)}
                    </span>
                    <Meta style={{ fontSize: 9, color: c.subtle }}>
                        {done}/{total}
                    </Meta>
                </Row>
            </Link>
        </div>

        <div style={{ flex: 1 }} />

        <Link to={R.account} style={{ textDecoration: 'none' }}>
            <Row style={{ gap: 11, margin: '0 12px', padding: '10px 12px', borderRadius: 12, border: hair }}>
                <Avatar score={index} size={30} />
                <Col style={{ flex: 1, minWidth: 0 }} gap={2}>
                    <span
                        style={{
                            fontSize: 11.5,
                            color: c.textPri,
                            fontWeight: 400,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {email || t('app.account.title')}
                    </span>
                    <Meta style={{ fontSize: 8.5, color: c.subtle }}>
                        {index == null ? t('app.shell.noTest') : t('app.shell.index', { index })}
                    </Meta>
                </Col>
                <Icon k="chev" size={11} color={c.subtle} />
            </Row>
        </Link>
    </Col>
);

export interface AppShellProps {
    active: TabId;
    /** Header title on desktop. Mobile screens render their own H1. */
    title: string;
    subtitle?: string;
    /** Right side of the desktop header — streak, day navigator, contextual chips. */
    chips?: React.ReactNode;
    /** Mobile-only header strip. */
    mobileHeader?: React.ReactNode;
    index?: number | null;
    email?: string;
    tasksDone?: number;
    tasksTotal?: number;
    children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
    active,
    title,
    subtitle,
    chips,
    mobileHeader,
    index = null,
    email,
    tasksDone = 0,
    tasksTotal = 0,
    children,
}) => {
    const desktop = useIsDesktop(DESKTOP);
    const t = useT();
    const { pathname } = useLocation();

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    const frame: React.CSSProperties = {
        minHeight: '100vh',
        background: c.bg,
        color: c.text,
        fontFamily: f.ui,
        WebkitFontSmoothing: 'antialiased',
        // Belt and braces: even if one child misbehaves, the page itself never
        // gains a horizontal scrollbar.
        overflowX: 'hidden',
    };

    if (!desktop) {
        return (
            // Installed to the home screen the app runs full-bleed under the status
            // bar, so the top inset has to be paid here. In a browser tab the inset
            // is 0 and nothing moves.
            <div style={{ ...frame, paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                {mobileHeader}
                <div style={{ paddingBottom: 108 }}>{children}</div>
                <TabBar active={active} done={tasksDone} total={tasksTotal} t={t} />
            </div>
        );
    }

    return (
        <div style={{ ...frame, display: 'flex', alignItems: 'flex-start' }}>
            <Sidebar active={active} done={tasksDone} total={tasksTotal} index={index} email={email} t={t} />
            <Col style={{ flex: 1, minWidth: 0 }}>
                <Row
                    style={{
                        height: 70,
                        padding: '0 34px',
                        borderBottom: hair,
                        gap: 16,
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        background: 'rgba(10,10,10,0.92)',
                        backdropFilter: 'blur(18px)',
                        WebkitBackdropFilter: 'blur(18px)',
                        zIndex: 20,
                    }}
                >
                    <Col gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 20, fontWeight: 400, letterSpacing: '-0.025em', color: c.textPri }}>
                            {title}
                        </span>
                        {subtitle && <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>{subtitle}</Meta>}
                    </Col>
                    <Row style={{ gap: 10, flex: '0 0 auto' }}>{chips}</Row>
                </Row>
                <div style={{ minWidth: 0 }}>{children}</div>
            </Col>
        </div>
    );
};

/** Desktop content gutter — matches the reference's 26/34px page padding. */
export const DeskRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Row style={{ padding: '26px 34px 40px', gap: 22, alignItems: 'flex-start', minWidth: 0, flexWrap: 'wrap' }}>
        {children}
    </Row>
);

/** Streak pill plus day navigator — the desktop header's right slot. */
export const HeaderStatus: React.FC<{ streak: number; day?: string }> = ({ streak, day }) => {
    const t = useT();

    return (
        <>
            {streak > 0 && (
                <Row style={{ gap: 6, padding: '7px 12px', borderRadius: 99, border: hair, background: c.card }}>
                    <Icon k="flame" size={13} color="#f59e0b" />
                    <span style={{ fontFamily: f.mono, fontSize: 12, color: c.textPri }}>{streak}</span>
                </Row>
            )}
            <Row style={{ gap: 9, padding: '7px 13px', borderRadius: 99, border: hair, background: c.card }}>
                <Icon k="back" size={12} color={c.muted} />
                <Meta style={{ fontSize: 10, color: c.textPri }}>{day || t('app.day.today')}</Meta>
                <Icon k="chev" size={12} color="rgba(232,230,224,0.2)" />
            </Row>
        </>
    );
};

/** Shared header strip for mobile screens that sit at the root of a tab. */
export const MobileHeader: React.FC<{ streak: number; index: number | null; day?: string }> = ({
    streak,
    index,
    day,
}) => {
    const t = useT();

    return (
        <Row style={{ padding: '14px 16px 2px', gap: 10 }}>
            {streak > 0 ? (
                <Row style={{ gap: 6, padding: '6px 11px', borderRadius: 99, border: hair, background: c.card }}>
                    <Icon k="flame" size={13} color="#f59e0b" />
                    <span style={{ fontFamily: f.mono, fontSize: 12, color: c.textPri }}>{streak}</span>
                </Row>
            ) : (
                <Meta style={{ fontSize: 10, border: hair, borderRadius: 99, padding: '6px 10px' }}>
                    {t('app.shell.day', { n: 1 })}
                </Meta>
            )}
            <Row style={{ flex: 1, justifyContent: 'center', gap: 13 }}>
                <Icon k="back" size={13} color={c.muted} />
                <Meta style={{ color: c.textPri, letterSpacing: '0.2em' }}>{day || t('app.day.today')}</Meta>
                <Icon k="chev" size={13} color="rgba(232,230,224,0.2)" />
            </Row>
            <Link to={R.account} style={{ textDecoration: 'none' }}>
                <Avatar score={index} />
            </Link>
        </Row>
    );
};
